import classNames from 'classnames';
import React, { MouseEventHandler, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { ChangeOwnershipPanelTypeMapping, useChangeOwnershipStore } from 'src/common/components/change-ownership/store';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { Journey } from 'src/common/interfaces/journey.interface';
import { OrganizationUser } from 'src/common/interfaces/organization/user.interface';
import JourneySlideover from 'src/common/JourneySlideOver';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { CheckmarkStrongIcon } from 'src/monet/icons';
import { useSlideOverStore } from 'src/store/slide-over';
import { useCurrentUser } from 'src/store/user';
import { apiUpdateJourney } from 'src/utils/journeyApi';
import { shallow } from 'zustand/shallow';

interface ChangeOwnershipContentProps extends ChangeOwnershipSlideoverProps {
  journey: Journey;
  members: OrganizationUser[];
  allowMultipleEditing: boolean;
  disableEditing: boolean;
  onNewCreatorSelect: () => void;
  onError: (error: string) => void;
  onComplete: (journeyUUID: Journey['uuid']) => void;
}

const ChangeOwnershipContent = ({
  journey,
  members,
  allowMultipleEditing,
  onUpdateJourney,
  disableEditing,
  onNewCreatorSelect,
  onComplete,
  onError,
}: ChangeOwnershipContentProps) => {
  const onNewCreatorSelected =
    (disableEditing: boolean): MouseEventHandler =>
    async (event) => {
      const target = event.target as HTMLElement;
      const userId = target.getAttribute('data-userId');
      if (!userId) {
        return;
      }

      const newCreator = members.find((user) => user.id === parseInt(userId, 10));
      if (!newCreator || disableEditing || newCreator.uuid === journey.creator.uuid) {
        return;
      }

      try {
        onNewCreatorSelect();
        const updatedJourney = await apiUpdateJourney(journey.uuid, {
          new_creator_id: newCreator.id,
          journey_workspace_id: journey.workspace.id,
        });
        onUpdateJourney(updatedJourney);
        onComplete(journey.uuid);
      } catch (err) {
        console.error(err);
        const { message } = err as Error;
        onError(message);
      }
    };

  const renderMembers = ({ disableEditing }: { disableEditing: boolean }) => {
    return (
      <>
        <span className='text-bedrock-p-strong'>Select the new creator</span>
        <div className='flex flex-col gap-2 w-3/4' onClick={onNewCreatorSelected(disableEditing)}>
          {members.map((user) => {
            const isUserCreator = user.id === journey.creator.id;
            return (
              <div key={user.email} data-userId={user.id} className='flex gap-2 text-bedrock-p group cursor-pointer'>
                <CheckmarkStrongIcon
                  className={classNames('w-4 h-4 my-auto transition-colors pointer-events-none', {
                    'text-transparent group-hover:text-black/50': !isUserCreator,
                    'text-bedrock-black': isUserCreator,
                  })}
                />
                <span className='select-none pointer-events-none'>{user.name}</span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div
      className={classNames('flex flex-col text-bedrock-p', {
        'gap-6': !allowMultipleEditing,
        'gap-2': allowMultipleEditing,
      })}
    >
      <div className='flex flex-col gap-2'>
        <span className='text-bedrock-p-strong'>Journey</span>
        <span>{journey.name}</span>
      </div>
      <div
        className={classNames('flex flex-col text-bedrock-p gap-2', {
          'pointer-events-none opacity-50': disableEditing,
        })}
      >
        {renderMembers({ disableEditing })}
      </div>
    </div>
  );
};

interface ChangeOwnershipSlideoverProps {
  onUpdateJourney: (journey: Journey) => void;
}

export const ChangeOwnershipSlideover = ({ onUpdateJourney }: ChangeOwnershipSlideoverProps) => {
  const isSlideoverOpen = useSlideOverStore((state) => state.isSlideOverOpen);
  const closeSlideOver = useSlideOverStore((state) => state.closeSlideOver);
  const [updatedJourneyUUIDs, setUpdatedJourneyUUIDs] = useState<Journey['uuid'][]>([]);
  const currentUser = useCurrentUser((state) => state.currentUser);
  const [loading, setLoading] = useState(false);

  const {
    isSlideOverOpenedFromDashboard,
    resetChangeOwnershipStore,
    journeyMapping,
    userNameToBeRevoked,
    changingOwnershipCallback,
  } = useChangeOwnershipStore(
    (state) => ({
      isSlideOverOpenedFromDashboard: state.panelType === ChangeOwnershipPanelTypeMapping.DASHBOARD,
      resetChangeOwnershipStore: state.reset,
      journeyMapping: state.mapping,
      changingOwnershipCallback: state.callback,
      userNameToBeRevoked: state.userNameToBeRevoked,
    }),
    shallow
  );

  const { setSuccessNotification, setErrorNotification } = useNotificationMessage();

  const journeysArr = Array.from(journeyMapping.entries()).filter(([_, { members }]) => members.length > 0);

  const onJourneyOwnerShipUpdated = (journey: Journey) => {
    try {
      onUpdateJourney(journey);
      setSuccessNotification(`The journey's creator is updated as ${journey.creator.name}.`);
    } catch (err) {
      console.error(err);
      setErrorNotification(`Failed to update the creator of the journey.`);
    }
  };

  const onSlideOverClosed = () => {
    closeSlideOver();
    resetChangeOwnershipStore();
  };

  const onChangeOwnershipComplete = (journeyUUID: Journey['uuid']) => {
    setLoading(false);
    if (isSlideOverOpenedFromDashboard) {
      closeSlideOver();
      return;
    }
    setUpdatedJourneyUUIDs((prev) => [...prev, journeyUUID]);
  };

  useUpdateEffect(() => {
    if (isSlideOverOpenedFromDashboard) {
      return;
    }
    if (journeysArr.every(([journeyUUID]) => updatedJourneyUUIDs.includes(journeyUUID))) {
      closeSlideOver();
      changingOwnershipCallback();
    }
  }, [updatedJourneyUUIDs, isSlideOverOpenedFromDashboard]);

  const renderCopy = () => {
    if (isSlideOverOpenedFromDashboard) {
      return (
        <span className='text-bedrock-p'>
          Once you update the Journey's creator, you won't be able to change it again, but the new owner will be.
        </span>
      );
    }

    return (
      <div className='flex flex-col gap-2 text-bedrock-p'>
        <span>
          Before revoking {userNameToBeRevoked || 'the user'}, you need to assign a new creator for each Journey.
        </span>
        <span>
          Select the new creator for each Journey. Once you complete assigning new creators, the user will be revoked
          from the organization.
        </span>
      </div>
    );
  };

  const getIfEditingJourneyDisabled = (journey: Journey) => {
    if (isSlideOverOpenedFromDashboard) {
      return !!currentUser && journey.creator.uuid !== currentUser.uuid;
    }

    return updatedJourneyUUIDs.includes(journey.uuid);
  };

  const getSlideOverTitle = () => {
    if (isSlideOverOpenedFromDashboard) {
      return 'Update creator';
    }

    return `Assign new creators`;
  };

  return (
    <JourneySlideover
      title={getSlideOverTitle()}
      open={isSlideoverOpen}
      onClose={onSlideOverClosed}
      maxWidth='xs'
      panelClassName={classNames({
        'w-[368px]': !isSlideOverOpenedFromDashboard,
        'pointer-events-none': loading,
      })}
      isBedrock
      skipClosingOnboarding
    >
      <>
        <div className='flex flex-col gap-6'>
          {renderCopy()}
          {journeysArr.map(([journeyId, { journey, members }]) => (
            <ChangeOwnershipContent
              key={`journey-creator-${journeyId}`}
              journey={journey}
              members={members}
              onNewCreatorSelect={() => {
                setLoading(true);
              }}
              onError={(message) => {
                setLoading(false);
                setErrorNotification(message);
              }}
              allowMultipleEditing={!isSlideOverOpenedFromDashboard}
              onUpdateJourney={onJourneyOwnerShipUpdated}
              onComplete={onChangeOwnershipComplete}
              disableEditing={getIfEditingJourneyDisabled(journey)}
            />
          ))}
        </div>
        {loading && (
          <div className='fixed w-[368px] top-0 right-0 left-[calc(100%-368px)] h-screen bg-white bg-opacity-75'>
            <FixedLoadingSpinner position='absolute' />
          </div>
        )}
      </>
    </JourneySlideover>
  );
};
